import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import healthService from '../services/healthService';
import StaffChat from '../components/communication/StaffChat';
import {
  FirstAidKit, Syringe, HeartStraight, User, Envelope, Briefcase, ChatCircle
} from '@phosphor-icons/react';

const todayISO = () => new Date().toISOString().split('T')[0];

function NursePortalPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileCount, setProfileCount] = useState(0);
  const [visitsToday, setVisitsToday] = useState(0);
  const [immunizationCount, setImmunizationCount] = useState(0);
  const [tab, setTab] = useState('dashboard');

  useEffect(() => {
    if (user && user.role !== 'nurse') {
      toast.error('You do not have access to this page');
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.school_id) return;

    Promise.all([
      healthService.listProfiles(),
      healthService.listVisits(),
      healthService.listImmunizations(),
    ])
      .then(([profiles, visits, immunizations]) => {
        setProfileCount(Array.isArray(profiles) ? profiles.length : 0);
        const today = todayISO();
        const visitsList = Array.isArray(visits) ? visits : [];
        setVisitsToday(visitsList.filter(v => (v.visit_date || '').startsWith(today)).length);
        setImmunizationCount(Array.isArray(immunizations) ? immunizations.length : 0);
      })
      .catch(err => {
        console.error(err);
        toast.error('Failed to load health overview');
      })
      .finally(() => setLoading(false));
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="page">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading nurse portal...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Health Portal</h1>
            <p>Student health profiles, clinic visits, and immunizations</p>
          </div>
          <div className="header-actions">
            <div className="user-info">
              <User size={20} />
              <span>{user?.first_name} {user?.last_name}</span>
            </div>
          </div>
        </div>

        <div className="tabs-container">
          <button className={`tab-btn ${tab === 'dashboard' ? 'active' : ''}`} onClick={() => setTab('dashboard')}>
            <Briefcase size={18} /> Dashboard
          </button>
          <button className={`tab-btn ${tab === 'messages' ? 'active' : ''}`} onClick={() => setTab('messages')}>
            <ChatCircle size={18} /> Messages
          </button>
        </div>

        {tab === 'messages' ? <StaffChat /> : (
        <>
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>My Profile</h3>
            <div className="profile-info">
              <div className="profile-item">
                <div className="profile-icon"><User size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Name</div>
                  <div className="profile-value">{user?.first_name} {user?.last_name}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Envelope size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Email</div>
                  <div className="profile-value">{user?.email}</div>
                </div>
              </div>
              <div className="profile-item">
                <div className="profile-icon"><Briefcase size={20} /></div>
                <div className="profile-content">
                  <div className="profile-label">Role</div>
                  <div className="profile-value">Nurse</div>
                </div>
              </div>
            </div>
          </div>

          <div className="dashboard-card">
            <h3>Clinic Overview</h3>
            <div className="stats-grid">
              <div className="stat">
                <div className="stat-value">{profileCount}</div>
                <div className="stat-label">Health Profiles</div>
              </div>
              <div className="stat">
                <div className="stat-value">{visitsToday}</div>
                <div className="stat-label">Visits Today</div>
              </div>
              <div className="stat">
                <div className="stat-value">{immunizationCount}</div>
                <div className="stat-label">Immunizations</div>
              </div>
            </div>
          </div>
        </div>

        <div className="quick-actions">
          <h3>Quick Actions</h3>
          <div className="actions-grid">
            <button className="action-btn" onClick={() => navigate('/health/profiles')}>
              <FirstAidKit size={24} />
              <span>Health Profiles</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/health/visits')}>
              <HeartStraight size={24} />
              <span>Clinic Visits</span>
            </button>
            <button className="action-btn" onClick={() => navigate('/health/immunizations')}>
              <Syringe size={24} />
              <span>Immunizations</span>
            </button>
          </div>
        </div>
        </>
        )}
      </div>
    </Layout>
  );
}

export default NursePortalPage;
